import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Activity, CheckCircle, Gauge, Network, XCircle } from 'lucide-react'
import ResponsiveDialog from '../../../components/shared/ResponsiveDialog'
import DiagnosticsTcpTargetFields from '../../../components/shared/DiagnosticsTcpTargetFields'
import type {
  SSHConnectionDiagnosticsProbeResult,
  SSHConnectionDiagnosticsRequest,
  SSHConnectionDiagnosticsResponse,
  SSHConnectionDiagnosticsThroughputResult,
  SSHConnectionDiagnosticsTcpResult,
} from '../../../services/api'
import type { SSHConnection } from '../types'

const DEFAULT_TIMEOUT_SECONDS = 5
const DEFAULT_TARGET_TIMEOUT_SECONDS = 3
const DEFAULT_SPEED_PROBE_BYTES = 262144

function parsePort(value: string): number | null {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const port = Number.parseInt(trimmed, 10)
  return Number.isInteger(port) && port >= 1 && port <= 65535 ? port : null
}

function parseBoundedNumber(value: string, min: number, max: number): number | null {
  const parsed = Number.parseFloat(value.trim())
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : null
}

function parseProbeBytes(value: string): number | null {
  const parsed = Number.parseInt(value.trim(), 10)
  return Number.isInteger(parsed) && parsed >= 65536 && parsed <= 5 * 1024 * 1024 ? parsed : null
}

function getTargetError(host: string, port: string, timeout: string, t: TFunction): string | null {
  if (!host.trim()) return null
  if (parsePort(port) === null) {
    return t('sshConnections.diagnostics.errors.targetPortRange', {
      defaultValue: 'Enter a TCP port between 1 and 65535.',
    })
  }
  if (parseBoundedNumber(timeout, 0.5, 15) === null) {
    return t('sshConnections.diagnostics.errors.targetTimeoutRange', {
      defaultValue: 'Enter a target timeout between 0.5 and 15 seconds.',
    })
  }
  return null
}

function formatElapsed(value: number | null | undefined, t: TFunction): string {
  return typeof value === 'number'
    ? `${Math.round(value)} ms`
    : t('sshConnections.diagnostics.notReported', { defaultValue: 'Not reported' })
}

function formatBytes(value: number | null | undefined, t: TFunction): string {
  if (typeof value !== 'number') {
    return t('sshConnections.diagnostics.notReported', { defaultValue: 'Not reported' })
  }
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`
  return `${(value / 1024 / 1024).toFixed(2)} MB`
}

function probeStatusColor(status?: string): 'default' | 'success' | 'error' | 'warning' | 'info' {
  if (status === 'success') return 'success'
  if (status === 'timeout') return 'warning'
  if (status === 'failed') return 'error'
  return 'default'
}

function sessionLabel(status: string, t: TFunction): string {
  if (status === 'success') return t('sshConnections.diagnostics.sessionHealthy')
  if (status === 'timeout') return t('sshConnections.diagnostics.sessionTimedOut')
  return t('sshConnections.diagnostics.sessionFailed')
}

function tcpLabel(status: string, t: TFunction): string {
  if (status === 'success') return t('sshConnections.diagnostics.tcpReachable')
  if (status === 'timeout') return t('sshConnections.diagnostics.tcpTimedOut')
  return t('sshConnections.diagnostics.tcpFailed')
}

function throughputLabel(status: string, t: TFunction): string {
  if (status === 'success') return t('sshConnections.diagnostics.speedComplete')
  if (status === 'timeout') return t('sshConnections.diagnostics.speedTimedOut')
  return t('sshConnections.diagnostics.speedFailed')
}

function buildPayload(
  targetHost: string,
  targetPort: string,
  targetTimeout: string,
  timeoutSeconds: string,
  speedProbeBytes: string
): SSHConnectionDiagnosticsRequest {
  const payload: SSHConnectionDiagnosticsRequest = {
    timeout_seconds: parseBoundedNumber(timeoutSeconds, 1, 30) ?? DEFAULT_TIMEOUT_SECONDS,
    speed_probe_bytes: parseProbeBytes(speedProbeBytes) ?? DEFAULT_SPEED_PROBE_BYTES,
  }
  const host = targetHost.trim()
  if (!host) return payload
  const port = parsePort(targetPort)
  const timeout = parseBoundedNumber(targetTimeout, 0.5, 15)
  if (port === null || timeout === null) return payload
  payload.target = { host, port, timeout_seconds: timeout }
  return payload
}

function DiagnosticRow({
  icon,
  title,
  status,
  value,
  detail,
}: {
  icon: ReactNode
  title: string
  status: string
  value?: string
  detail?: string | null
}) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ color: status === 'success' ? 'success.main' : 'text.secondary', mt: 0.25 }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography fontWeight={700} variant="body2">
              {title}
            </Typography>
            <Chip size="small" color={probeStatusColor(status)} label={status} />
          </Stack>
          {value && (
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </Typography>
          )}
          {detail && (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {detail}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

function errorDetail(result: SSHConnectionDiagnosticsProbeResult): string | null {
  if (!result.error && !result.message) return null
  if (result.error && result.message) return `${result.error}: ${result.message}`
  return result.error || result.message || null
}

function tcpDetail(result: SSHConnectionDiagnosticsTcpResult): string {
  const target = `${result.target.host}:${result.target.port}`
  const error = errorDetail(result)
  return error ? `${target} - ${error}` : target
}

function throughputValue(result: SSHConnectionDiagnosticsThroughputResult, t: TFunction): string {
  if (result.status === 'success' && typeof result.mbps === 'number') {
    return `${result.mbps.toFixed(2)} MB/s`
  }
  return (
    errorDetail(result) ||
    t('sshConnections.diagnostics.noThroughputResult', { defaultValue: 'No throughput result' })
  )
}

function throughputDetail(result: SSHConnectionDiagnosticsThroughputResult, t: TFunction): string {
  if (result.status !== 'success') return errorDetail(result) || ''
  return t('sshConnections.diagnostics.transferredIn', {
    bytes: formatBytes(result.bytes_transferred, t),
    elapsed: formatElapsed(result.elapsed_ms, t),
    defaultValue: '{{bytes}} in {{elapsed}}',
  })
}

export function ConnectionDiagnosticsDialog({
  connection,
  open,
  initialResult = null,
  onClose,
  onRunDiagnostics,
}: {
  connection: SSHConnection | null
  open: boolean
  initialResult?: SSHConnectionDiagnosticsResponse | null
  onClose: () => void
  onRunDiagnostics?: (
    connection: SSHConnection,
    payload: SSHConnectionDiagnosticsRequest
  ) => Promise<SSHConnectionDiagnosticsResponse>
}) {
  const { t } = useTranslation()
  const [targetHost, setTargetHost] = useState('')
  const [targetPort, setTargetPort] = useState('')
  const [targetTimeout, setTargetTimeout] = useState(String(DEFAULT_TARGET_TIMEOUT_SECONDS))
  const [timeoutSeconds, setTimeoutSeconds] = useState(String(DEFAULT_TIMEOUT_SECONDS))
  const [speedProbeBytes, setSpeedProbeBytes] = useState(String(DEFAULT_SPEED_PROBE_BYTES))
  const [result, setResult] = useState<SSHConnectionDiagnosticsResponse | null>(initialResult)
  const [running, setRunning] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setResult(initialResult)
    setRunning(false)
    setErrorMessage(null)
  }, [initialResult, open])

  const targetError = getTargetError(targetHost, targetPort, targetTimeout, t)
  const timeoutError =
    parseBoundedNumber(timeoutSeconds, 1, 30) === null
      ? t('sshConnections.diagnostics.errors.sshTimeoutRange', {
          defaultValue: 'Enter an SSH timeout between 1 and 30 seconds.',
        })
      : null
  const speedError =
    parseProbeBytes(speedProbeBytes) === null
      ? t('sshConnections.diagnostics.errors.speedProbeRange', {
          defaultValue: 'Enter a speed probe size between 65536 and 5242880 bytes.',
        })
      : null
  const formError = targetError || timeoutError || speedError

  const runDiagnostics = async () => {
    if (!connection || !onRunDiagnostics || formError) return
    setRunning(true)
    setErrorMessage(null)
    try {
      const nextResult = await onRunDiagnostics(
        connection,
        buildPayload(targetHost, targetPort, targetTimeout, timeoutSeconds, speedProbeBytes)
      )
      setResult(nextResult)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : t('sshConnections.toasts.diagnosticsFailed')
      )
    } finally {
      setRunning(false)
    }
  }

  const footer = (
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose}>{t('common.buttons.close', { defaultValue: 'Close' })}</Button>
      <Button
        variant="contained"
        onClick={() => void runDiagnostics()}
        disabled={!connection || !onRunDiagnostics || running || Boolean(formError)}
        startIcon={
          running ? <CircularProgress color="inherit" size={16} /> : <Activity size={16} />
        }
      >
        {running
          ? t('sshConnections.diagnostics.running')
          : t('sshConnections.diagnostics.runCheck')}
      </Button>
    </DialogActions>
  )

  return (
    <ResponsiveDialog open={open} onClose={onClose} fullWidth maxWidth="md" footer={footer}>
      <DialogTitle>{t('sshConnections.diagnostics.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 0.5, pb: 1 }}>
          <Box>
            <Typography fontWeight={700}>
              {connection
                ? `${connection.username}@${connection.host}:${connection.port}`
                : t('sshConnections.diagnostics.noConnection')}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
              {t('sshConnections.diagnostics.description')}
            </Typography>
          </Box>

          <DiagnosticsTcpTargetFields
            targetHost={targetHost}
            targetPort={targetPort}
            targetTimeout={targetTimeout}
            onTargetHostChange={setTargetHost}
            onTargetPortChange={setTargetPort}
            onTargetTimeoutChange={setTargetTimeout}
            hasTarget={Boolean(targetHost.trim())}
            portInvalid={Boolean(targetHost.trim()) && parsePort(targetPort) === null}
            timeoutInvalid={
              Boolean(targetHost.trim()) && parseBoundedNumber(targetTimeout, 0.5, 15) === null
            }
            timeoutInputProps={{ min: 0.5, max: 15, step: 0.5 }}
            labels={{
              summary: t('sshConnections.diagnostics.advancedTargetSummary', {
                defaultValue: 'Advanced: test another service',
              }),
              description: t('sshConnections.diagnostics.advancedTargetDescription', {
                defaultValue:
                  'Checks whether this remote machine can reach a separate service. Leave blank for normal diagnostics.',
              }),
              host: t('sshConnections.diagnostics.serviceHost', {
                defaultValue: 'Service host',
              }),
              hostPlaceholder: t('sshConnections.diagnostics.serviceHostPlaceholder', {
                defaultValue: 'postgres.internal',
              }),
              hostHelper: t('sshConnections.diagnostics.serviceHostHelper', {
                defaultValue: 'Optional service to test from this remote machine',
              }),
              port: t('sshConnections.diagnostics.servicePort', {
                defaultValue: 'Service port',
              }),
              portPlaceholder: t('sshConnections.diagnostics.servicePortPlaceholder', {
                defaultValue: '5432',
              }),
              portError: t('sshConnections.diagnostics.servicePortRangeShort', {
                defaultValue: '1-65535',
              }),
              timeout: t('sshConnections.diagnostics.targetTimeout'),
              timeoutHelper: t('sshConnections.diagnostics.seconds'),
              timeoutError: t('sshConnections.diagnostics.serviceTimeoutRangeShort', {
                defaultValue: '0.5-15 seconds',
              }),
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
              gap: 1.25,
            }}
          >
            <TextField
              label={t('sshConnections.diagnostics.sshTimeout')}
              value={timeoutSeconds}
              onChange={(event) => setTimeoutSeconds(event.target.value)}
              size="small"
              type="number"
              inputProps={{ min: 1, max: 30, step: 1 }}
              error={Boolean(timeoutError)}
              helperText={t('sshConnections.diagnostics.seconds')}
            />
            <TextField
              label={t('sshConnections.diagnostics.speedProbeSize')}
              value={speedProbeBytes}
              onChange={(event) => setSpeedProbeBytes(event.target.value)}
              size="small"
              type="number"
              inputProps={{ min: 65536, max: 5 * 1024 * 1024, step: 65536 }}
              error={Boolean(speedError)}
              helperText={t('sshConnections.diagnostics.speedProbeHelper')}
            />
          </Box>

          {formError && (
            <Alert severity="warning" role="alert" sx={{ borderRadius: 1.5 }}>
              {formError}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" role="alert" sx={{ borderRadius: 1.5 }}>
              {errorMessage}
            </Alert>
          )}

          {result && (
            <Stack spacing={1.25}>
              <DiagnosticRow
                icon={<CheckCircle size={18} />}
                title={sessionLabel(result.session.status, t)}
                status={result.session.status}
                value={formatElapsed(result.session.elapsed_ms, t)}
                detail={errorDetail(result.session) || result.session.output}
              />
              <DiagnosticRow
                icon={<Activity size={18} />}
                title={t('sshConnections.diagnostics.latency')}
                status={result.latency.status}
                value={formatElapsed(result.latency.elapsed_ms, t)}
                detail={errorDetail(result.latency)}
              />
              {result.tcp && (
                <DiagnosticRow
                  icon={<Network size={18} />}
                  title={tcpLabel(result.tcp.status, t)}
                  status={result.tcp.status}
                  value={formatElapsed(result.tcp.elapsed_ms, t)}
                  detail={tcpDetail(result.tcp)}
                />
              )}
              {result.throughput && (
                <DiagnosticRow
                  icon={
                    result.throughput.status === 'success' ? (
                      <Gauge size={18} />
                    ) : (
                      <XCircle size={18} />
                    )
                  }
                  title={throughputLabel(result.throughput.status, t)}
                  status={result.throughput.status}
                  value={throughputValue(result.throughput, t)}
                  detail={throughputDetail(result.throughput, t)}
                />
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </ResponsiveDialog>
  )
}
